import React, { useEffect, useRef, useState } from 'react';
import "./TradeAmountCard.scss";
import { useSelector } from 'react-redux';
import { parseJSON, toFix } from '../../../../helpers/commonHelper';
import CustomTooltip from "../../../../components/common/CustomTooltip/CustomTooltip.jsx";
import { InfoIcon } from "../../../../assets/Svg/SvgImages.jsx";
import { ATS } from '../../../../features/ats/atsSlice';
import { resolveLinkedWalletType } from '../../../../services/wallet/linkedWallet.utils';
import { WALLET_PROVIDER } from '../../../../constants/wallet.constants';
import { formatPlainDecimal, multiplyPlainDecimals } from '../placeOrder.utils';
import ButtonTriangleSpinner from "../../../../components/common/ButtonTriangleSpinner/ButtonTriangleSpinner.jsx";
const PLACE_ORDER_MAX_DECIMALS = 4;
const GAS_FEE_DEBOUNCE_MS = 500;

/** Query value for estimated_gas_fee — BitGo / MetaMask. */
const resolveGasFeeWalletType = wallet => {
  if (!wallet) return '';
  const label = String(wallet?.walletTypeLabel ?? '').trim();
  if (label) return label;
  const raw = String(resolveLinkedWalletType(wallet) ?? wallet?.provider ?? '').trim().toLowerCase();
  if (raw.includes('metamask') || raw === WALLET_PROVIDER.METAMASK) {
    return 'MetaMask';
  }
  if (raw.includes('bitgo') || raw === WALLET_PROVIDER.BITGO) {
    return 'BitGo';
  }
  return label || '';
};
function tradeAmountDecimalPlaces(selectedPair, priceGt0, qtyGt0) {
  if (!priceGt0 || !qtyGt0) return null;
  const pd = Number(selectedPair?.price_decimal);
  const qd = Number(selectedPair?.quantity_decimal);
  const effPd = Number.isFinite(pd) && pd > 0 ? Math.min(pd, PLACE_ORDER_MAX_DECIMALS) : PLACE_ORDER_MAX_DECIMALS;
  const effQd = Number.isFinite(qd) && qd > 0 ? Math.min(qd, PLACE_ORDER_MAX_DECIMALS) : PLACE_ORDER_MAX_DECIMALS;
  return Math.min(effPd + effQd, 12);
}
function legacyCommissionDecimals(selectedPair, priceGt0, qtyGt0) {
  if (!priceGt0 || !qtyGt0) return null;
  const pd = Number(selectedPair?.price_decimal);
  return Number.isFinite(pd) && pd > 0 ? pd : PLACE_ORDER_MAX_DECIMALS;
}

/** Numeric trade amount used for gas fee estimate (buy: price×qty, sell: qty). */
function resolveTradeAmountForGas(side, price, quantity) {
  const p = Number(price);
  const q = Number(quantity);
  if (side === 0) {
    if (!(p > 0) || !(q > 0)) return 0;
    return p * q;
  }
  return q > 0 ? q : 0;
}
const EMPTY_TRADE_OPTIONS = {
  tradeAmount: 0,
  commission: 0,
  gasPrice: '0'
};
const EMPTY_GAS_FEE = {
  amount: '0',
  feeCoin: '',
  chainName: ''
};
const TradeAmountCard = ({
  side,
  formData,
  resetKey = 0,
  onGasFeeChange
}) => {
  const [options, setOptions] = useState(EMPTY_TRADE_OPTIONS);
  const optionRef = useRef(options);
  optionRef.current = options;
  const [gasCoin, setGasCoin] = useState("");
  const [gasChainName, setGasChainName] = useState("");
  const [isCalculatingGas, setIsCalculatingGas] = useState(false);
  const gasRequestIdRef = useRef(0);
  const selectedPair = useSelector(state => state.ats.selectedPair);
  const selectedTradeWallet = useSelector(state => state.ats.selectedTradeWallet);
  const onGasFeeChangeRef = useRef(onGasFeeChange);
  onGasFeeChangeRef.current = onGasFeeChange;
  const emitGasFee = next => {
    onGasFeeChangeRef.current?.(next);
  };
  const clearGasFee = () => {
    gasRequestIdRef.current += 1;
    setIsCalculatingGas(false);
    const obj = parseJSON(optionRef.current);
    obj.gasPrice = '0';
    setOptions(parseJSON(obj));
    setGasChainName("");
    emitGasFee(EMPTY_GAS_FEE);
  };
  useEffect(() => {
    if (!resetKey) return;
    setOptions(EMPTY_TRADE_OPTIONS);
    optionRef.current = EMPTY_TRADE_OPTIONS;
    setIsCalculatingGas(false);
    emitGasFee(EMPTY_GAS_FEE);
  }, [resetKey]);
  useEffect(() => {
    const values = parseJSON(formData);
    if (values) {
      if (!values.price) values.price = 0;
      if (!values.quantity) values.quantity = 0;
      const priceGt0 = values.price > 0;
      const qtyGt0 = values.quantity > 0;
      if (!priceGt0 && !qtyGt0) {
        setOptions(EMPTY_TRADE_OPTIONS);
        optionRef.current = EMPTY_TRADE_OPTIONS;
        clearGasFee();
        return;
      }
      const tradeDec = side === 0 ? tradeAmountDecimalPlaces(selectedPair, priceGt0, qtyGt0) : legacyCommissionDecimals(selectedPair, priceGt0, qtyGt0);
      const obj = parseJSON(options);
      if (values.price > 0 || values.quantity > 0) {
        const rawProduct = multiplyPlainDecimals(values.quantity, values.price, tradeDec === null ? 12 : tradeDec);
        if (side === 0) {
          obj.tradeAmount = rawProduct;
        } else {
          obj.tradeAmount = values.quantity;
        }
        if (selectedPair) {
          const fee = side === 0 ? selectedPair.trade_fee_buy : selectedPair.trade_fee_sell;
          const commissionBase = side === 0 ? rawProduct : String(values.quantity ?? '');
          const commissionDec = tradeDec === null ? undefined : tradeDec;
          obj.commission = toFix(Number(commissionBase) * (fee / 100), commissionDec);
        }
      }
      setOptions(obj);
      optionRef.current = obj;
    }
  }, [formData, side, selectedPair]);
  const getGasCoinBySide = () => {
    if (!selectedPair) return "";
    const pairKey = selectedPair?.pair_key || "";
    const normalizedPair = pairKey.toLowerCase().replace("/", "_");
    const pairCoins = normalizedPair.split("_").filter(Boolean);
    const buyCoin = pairCoins[0] || String(selectedPair?.otherCurrency || "").toLowerCase();
    return buyCoin;
  };
  const updateGasFee = async (coin, tradeAmount, requestId) => {
    if (!coin || !(tradeAmount > 0)) return;
    const walletType = resolveGasFeeWalletType(selectedTradeWallet);
    const amountParam = formatPlainDecimal(tradeAmount);
    const response = await ATS.getEstimatedGasFee(coin, walletType || undefined, amountParam);
    if (requestId !== gasRequestIdRef.current) return;
    if (response?.status === 200) {
      const fee = formatPlainDecimal(response?.data?.approximateFeeInCoin);
      const chainName = response?.data?.chainName || "";
      const feeCoin = String(response?.data?.feeCoin || "").trim();
      const obj = parseJSON(optionRef.current);
      obj.gasPrice = fee;
      setOptions(parseJSON(obj));
      setGasChainName(String(chainName || "").toUpperCase());
      emitGasFee({
        amount: fee,
        feeCoin,
        chainName
      });
    } else {
      const obj = parseJSON(optionRef.current);
      obj.gasPrice = '0';
      setOptions(parseJSON(obj));
      setGasChainName("");
      emitGasFee(EMPTY_GAS_FEE);
    }
    setIsCalculatingGas(false);
  };
  useEffect(() => {
    const coin = getGasCoinBySide();
    setGasCoin(coin.toUpperCase());
    setGasChainName("");
  }, [selectedPair, side]);
  useEffect(() => {
    const coin = getGasCoinBySide();
    const tradeAmount = resolveTradeAmountForGas(side, formData?.price, formData?.quantity);
    if (!coin || !(tradeAmount > 0)) {
      clearGasFee();
      return undefined;
    }
    setIsCalculatingGas(true);
    emitGasFee(EMPTY_GAS_FEE);
    const requestId = gasRequestIdRef.current + 1;
    gasRequestIdRef.current = requestId;
    const timeout = setTimeout(() => {
      updateGasFee(coin, tradeAmount, requestId);
    }, GAS_FEE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timeout);
    };
  }, [formData?.price, formData?.quantity, selectedPair, side, selectedTradeWallet]);
  const gasDisplay = () => {
    if (isCalculatingGas) {
      return <h6 className="tradeAmountcard__calculating" aria-busy="true" aria-label="Calculating fee">
                    <ButtonTriangleSpinner size={18} color="currentColor" />
                </h6>;
    }
    if (!(Number(options.tradeAmount) > 0)) {
      return <h6>0 &nbsp;{gasChainName || gasCoin || "ETH"}</h6>;
    }
    return <h6>
                {formatPlainDecimal(options.gasPrice)} &nbsp;{gasChainName || gasCoin || "ETH"}
            </h6>;
  };
  return <>
            <div className='tradeAmountcard'>
                <p>Trade Amount</p>
                <h6 title={`${formatPlainDecimal(options.tradeAmount)} ${selectedPair && side === 0 ? selectedPair?.baseCurrency : selectedPair?.otherCurrency || ''}`}>
                    {formatPlainDecimal(options.tradeAmount)}&nbsp;{selectedPair && side === 0 ? selectedPair?.baseCurrency : selectedPair?.otherCurrency}
                </h6>
            </div>
            <div className='tradeAmountcard'>
                <p>Commissions</p>
                <h6 title={`${formatPlainDecimal(options.commission)} ${selectedPair?.baseCurrency || ''}`}>
                    {formatPlainDecimal(options.commission)}&nbsp;{selectedPair ? selectedPair.baseCurrency : ''}
                </h6>
            </div>
            <div className='tradeAmountcard'>
                <p className='d-flex'>
                    Gas Fees
                    <CustomTooltip placement="right" icon={<InfoIcon />} className="ms-2" toolclass="gasFeestool" text={<p>
                            <strong>Estimated Gas Fees</strong>
                            <span className='d-block'>Oasis Pro Markets cannot guarntee executions of transactions or timing or executions based on gas fees paid as market conditions are continuously changing.</span>
                            <span className='d-block'>Similarly, the gas fees listed on the order management screen should only be regarded as an estimate due to continuous market changes.</span>
                            <span className='d-block'>Actual costs will be confirmed upon completion of each transaction</span>
                        </p>} />
                </p>
                {gasDisplay()}
            </div>
        </>;
};
export default TradeAmountCard;